import { useGetState } from "components/ReduxProvider";
import Dexie, { Table } from "dexie";
import { useDBLog } from "hooks/useDBLog";
import { useTranslation } from "react-i18next";
import { snackbar } from "tools/events";
import { useRef, useEffect, useCallback } from "react";
import { windowReloadLimited } from "tools/windowReload";
import { useNavigate } from "react-router";
import { MAIN } from "root/RootRouter.config";

export interface Grammar {
  id: string;
  grammar: string;
  lang: string;
}
export interface CardData {
  id?: number;
  text: string;
  tts?: string;
  ttsVoiceId: string;
  lang: string;
  grammar: readonly Grammar[] | null;
}

const dexieVersion = "1";
// NOTE: once we will need to update version and clear data on users devices, uncomment the code below:
// const storageDexieVersion = window.localStorage.getItem("dexieVerion");
// if (dexieVersion !== storageDexieVersion) {
//   Dexie.delete("myDatabase");
//   const dv = window.localStorage.setItem("dexieVerion", dexieVersion);
//   console.info("deleted old dexie version");
// }

export class MySubClassedDexie extends Dexie {
  cardsData!: Table<CardData>;

  constructor() {
    super("myDatabase");
    this.version(Number(dexieVersion))
      .stores({ cardsData: "++id" })
      .upgrade((trans) => trans.table("cardsData").clear());
  }
}

export const dexie = new MySubClassedDexie();

// Ensure only a single open() is in-flight to avoid DatabaseClosedError due to
// concurrent opens. Subsequent callers reuse the same promise until resolved.
let openPromise: Promise<void> | null = null;
export const reopenDexie = async (): Promise<void> => {
  if (dexie.isOpen()) return;
  if (openPromise) return openPromise;

  openPromise = (async () => {
    await dexie.open();
  })()
    .catch((err) => {
      openPromise = null;
      throw err;
    })
    .finally(() => {
      openPromise = null;
    });

  return openPromise;
};

export const withDexieRetry = async <T>(fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn();
  } catch (e: any) {
    const msg = String(e?.message || "");
    const isClosed = e?.name === "DatabaseClosedError";
    const isLost = /Connection to Indexed Database server lost/i.test(msg);
    if (isClosed || isLost) {
      try {
        await reopenDexie();
        return await fn();
      } catch {
        throw e;
      }
    }
    throw e;
  }
};

export const clearIndexDB = () => {
  // dexie.cards.clear(); // offline version
  return dexie.cardsData.clear();
};

export const deleteAllIndexedDBs = () => {
  // src: https://stackoverflow.com/a/54764150/844628
  window.indexedDB?.databases().then((r) => {
    for (var i = 0; i < r.length; i++) window.indexedDB.deleteDatabase(String(r[i].name));
  });
};

const CLEAR_DB_FLAG = "clearDBFlag";

export const setClearDBFlag = () => {
  window.localStorage.setItem(CLEAR_DB_FLAG, "true");
};

export const hasClearDBFlag = () => {
  return window.localStorage.getItem(CLEAR_DB_FLAG) === "true";
};

export const removeClearDBFlag = () => {
  window.localStorage.removeItem(CLEAR_DB_FLAG);
};

const isIdbQuotaExceeded = (err: any): boolean => {
  const isQuota = (e: any) => {
    if (!e) return false;
    if (e?.name === "QuotaExceededError") return true;
    try {
      if (typeof DOMException !== "undefined" && e instanceof DOMException && e.name === "QuotaExceededError")
        return true;
    } catch {}
    return false;
  };

  if (isQuota(err) || isQuota(err?.inner) || isQuota(err?.inner?.inner)) return true;
  const failures: any[] = Array.isArray(err?.failures) ? err.failures : [];
  if (failures.some((f) => isQuota(f) || isQuota(f?.inner))) return true;
  return false;
};

export const DEXIE_STORAGE_DBLOG = "storageError";

export const useDexieError = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cardsData = useGetState("idbCardsData");
  const dblog = useDBLog();

  const cardsDataRef = useRef<CardData[] | undefined>(undefined);

  useEffect(() => {
    cardsDataRef.current = cardsData;
  }, [cardsData]);

  return useCallback(
    async (error: any) => {
      const isQuotaExceeded = isIdbQuotaExceeded(error);

      dblog(
        DEXIE_STORAGE_DBLOG,
        `Dexie transaction failed. (${isQuotaExceeded ? "Alert to user + clearing IndexDB" : "Reload app + clearing IndexDB"})`,
        {
          error,
          cardsInDB: cardsDataRef.current?.length,
          cardsSizeInKB: Math.round(JSON.stringify(cardsDataRef.current).length / 1024),
          storageEstimate: navigator?.storage?.estimate && (await navigator.storage.estimate())
        }
      );

      if (isQuotaExceeded) {
        snackbar(t("Storage error. Try to restart the App and check if you have enough space on your device."), {
          severity: "error",
          autoHide: false
        });
        clearIndexDB();
        navigate(MAIN.url(), { replace: true });
      } else {
        // dexie cannot be cleared when connection is lost so we need to set a flag and reload app
        // clearing IndexDB will be done in DexieInitializer
        setClearDBFlag();
        windowReloadLimited(undefined, { reason: "DexieError" });
      }
    },
    [dblog, t, navigate]
  );
};
